import { useAuth } from "@clerk/react";
import { useState } from "react";
import { ApiError } from "@/features/api/apiClient";
import { redeemAccessCode } from "@/features/access/accessApi";

type AccessCodeRedemptionCardProps = {
  onSuccess: () => void;
};

function tierLabel(tier: string): string {
  if (tier === "lifetime_standard") return "Lifetime Standard";
  if (tier === "pro") return "Pro";
  return "Standard";
}

export function AccessCodeRedemptionCard({
  onSuccess,
}: AccessCodeRedemptionCardProps) {
  const { getToken } = useAuth();
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successTier, setSuccessTier] = useState<string | null>(null);

  const trimmedCode = code.trim();
  const canSubmit = trimmedCode.length > 0 && !isLoading && !successTier;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const result = await redeemAccessCode(token, trimmedCode);
      setSuccessTier(result.tierGranted);
      setCode("");
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Unable to redeem access code. Please try again.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-950">
        Have an access code?
      </h3>
      <p className="mt-1.5 max-w-xl text-sm leading-6 text-slate-600">
        Enter your code below and we'll apply the access linked to it.
      </p>

      {successTier ? (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-sm font-semibold text-emerald-900">
            Access code applied successfully.
          </p>
          <p className="mt-1 text-sm text-emerald-800">
            Your access has been updated to{" "}
            <span className="font-semibold">{tierLabel(successTier)}</span>. Your
            new access is now active.
          </p>
          <button
            type="button"
            onClick={() => setSuccessTier(null)}
            className="mt-3 text-xs font-semibold text-emerald-700 hover:text-emerald-900"
          >
            Redeem another code
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          {error ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            <label htmlFor="access-code" className="sr-only">
              Access code
            </label>
            <input
              id="access-code"
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value.toUpperCase());
                setError(null);
              }}
              placeholder="Access code"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              maxLength={50}
              className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white px-3 py-2 font-mono text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            />
            <button
              type="submit"
              disabled={!canSubmit}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Redeeming…" : "Redeem access code"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
